import { useEffect } from "react"
import { Route, Navigate, useLocation, useParams } from "react-router-dom"
import PPDashboard from "./dashboard/PPDashboard"
import PPPriceTable from "./pricetable/PPPriceTable"
import PPReports from "./PPReports"
import PPStatistics from "./statistics/PPStatistics"
import PPSettings from "./PPSettings"
import PPCrawler from "./PPCrawler"
import PPAdmin from "./PPAdmin"
import RuleEngineNotEnabledPage from "./RuleEngineNotEnabledPage"
import PPPriceAlertsSetting from "./alerts/PPPriceAlertsSetting"
import PPProviderSummary from "./PPProviderSummary"
import PPPriceManagementRules from "../pricemanagement/rules/PPPriceManagementRules"
import PPPriceManagementEditor from "../pricemanagement/editor/PPPriceManagementEditor"
import PPPriceManagementChecklist from "../pricemanagement/checklist/PPPriceManagementChecklist"
import PPPriceManagementHistory from "../pricemanagement/price-history/PPPriceManagementHistory"
import PPPriceManagementExcludedArticles from "../pricemanagement/excluded-articles/PPPriceManagementExcludedArticles"
import PPPriceManagementGlobalRules from "../pricemanagement/global-rules/PPPriceManagementGlobalRules"
import EditorProvider from "../pricemanagement/editor/hooks/EditorProvider"
import ProviderSummaryNotEnabledPage from "./ProviderSummaryNotEnabledPage"
import PPAI from "./ai/PPAI"
import PPAINotEnabledPage from "./ai/PPAINotEnabledPage"
import PPMatching from "./PPMatching"
import PPDematching from "./PPDematching"
import PPErrorBoundary from "../components/layout/PPErrorBoundary"

function RuleEditorRoute() {
    const { ruleId } = useParams()
    const location = useLocation()
    const templateRuleId = new URLSearchParams(location.search).get("templateRuleId")
    const editorKey = ruleId || templateRuleId || "new"

    return (
        <EditorProvider key={editorKey}>
            <PPPriceManagementEditor />
        </EditorProvider>
    )
}

const BLOG_URL = "https://preis-beobachtung.com/blog/"

function BlogRedirect() {
    useEffect(() => {
        window.location.assign(BLOG_URL)
    }, [])
    return null
}

function priceManPage(hasAccess, page, resetKey) {
    if (!hasAccess) {
        return <RuleEngineNotEnabledPage />
    }
    return (
        <PPErrorBoundary variant="section" resetKeys={[resetKey]}>
            {page}
        </PPErrorBoundary>
    )
}

const PRICETABLE_PATHS = [
    "/pricetable",
    "/pricetable/s/:search",
    "/pricetable/:filter/:where/:sortProperty",
    "/pricetable/:filter/:where/generic/:genericFilterProperty",
    "/pricetable/generic/:genericFilterProperty"
]

export function appInnerRouteList({
    userId,
    currentAuth,
    auth,
    setSelectedProductCell,
    setSelectedRowInfos,
    setProductCount,
    setPresetId,
    presetId,
    setRefetchPricetable,
    settingLocalstorage,
    setDataCurrentAuth,
    hasPriceManagementAccess
}) {
    const pricetable = (
        <PPPriceTable
            changeSelectedCell={setSelectedProductCell}
            changeSelectedRow={setSelectedRowInfos}
            productCountChange={setProductCount}
            changePresetId={setPresetId}
            presetId={presetId}
            userId={userId}
            setRefetchPricetable={setRefetchPricetable}
        />
    )

    return [
        <Route key="/statistics" path="/statistics" element={<PPStatistics />} />,
        <Route key="/reports" path="/reports" element={<PPReports userId={userId} />} />,
        <Route
            key="/settings"
            path="/settings"
            element={
                <PPSettings
                    changePresetId={setPresetId}
                    presetId={presetId}
                    settingLocalstorage={settingLocalstorage}
                    setDataCurrentAuth={setDataCurrentAuth}
                />
            }
        />,
        <Route key="/crawler" path="/crawler" element={auth?.isAdmin() ? <PPCrawler /> : ""} />,
        <Route key="/ppai" path="/ppai" element={auth.hasPpAiAccess() ? <PPAI /> : <PPAINotEnabledPage />} />,
        <Route key="/admin" path="/admin" element={<PPAdmin />} />,
        <Route key="/matching" path="/matching" element={<PPMatching />} />,
        <Route key="/extmatching" path="/extmatching" element={<PPMatching />} />,
        <Route key="/dematching" path="/dematching" element={<PPDematching />} />,
        <Route key="/extdematching" path="/extdematching" element={<PPDematching />} />,
        <Route
            key="/pricemanagement/rules"
            path="/pricemanagement/rules"
            element={priceManPage(hasPriceManagementAccess, <PPPriceManagementRules />, "rules")}
        />,
        <Route
            key="/pricemanagement/editor"
            path="/pricemanagement/editor"
            element={priceManPage(hasPriceManagementAccess, <RuleEditorRoute />, "editor")}
        />,
        <Route
            key="/pricemanagement/editor/:ruleId"
            path="/pricemanagement/editor/:ruleId"
            element={priceManPage(hasPriceManagementAccess, <RuleEditorRoute />, "editor-rule")}
        />,
        <Route
            key="/pricemanagement/checklist"
            path="/pricemanagement/checklist"
            element={priceManPage(hasPriceManagementAccess, <PPPriceManagementChecklist />, "checklist")}
        />,
        <Route
            key="/pricemanagement/history"
            path="/pricemanagement/history"
            element={priceManPage(hasPriceManagementAccess, <PPPriceManagementHistory />, "history")}
        />,
        <Route
            key="/pricemanagement/excludedArticles"
            path="/pricemanagement/excludedArticles"
            element={priceManPage(hasPriceManagementAccess, <PPPriceManagementExcludedArticles />, "excluded")}
        />,
        <Route
            key="/pricemanagement/globalRules"
            path="/pricemanagement/globalRules/:section?"
            element={priceManPage(hasPriceManagementAccess, <PPPriceManagementGlobalRules />, "global")}
        />,
        <Route key="/extpms" path="/extpms" element={<Navigate to="/pricemanagement/rules" replace />} />,
        <Route key="/pricemanagement/legacy" path="/pricemanagement/legacy" element={<Navigate to="/pricemanagement/rules" replace />} />,
        <Route
            key="/pricealerts"
            path="/pricealerts"
            element={<PPPriceAlertsSetting userId={userId} customerId={currentAuth?.user?.customerId} />}
        />,
        ...PRICETABLE_PATHS.map((path) => <Route path={path} element={pricetable} key={path} />),
        <Route key="/" path="/" element={<PPDashboard />} />,
        <Route
            key="/providerSummary"
            path="/providerSummary"
            element={auth?.hasProviderSummaryAccess() ? <PPProviderSummary /> : <ProviderSummaryNotEnabledPage />}
        />,
        <Route
            key="/providerSummary/:providerId"
            path="/providerSummary/:providerId"
            element={auth?.hasProviderSummaryAccess() ? <PPProviderSummary /> : <ProviderSummaryNotEnabledPage />}
        />,
        <Route key="/blog" path="/blog" element={<BlogRedirect />} />
    ]
}
